import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
/** @jsx jsx */
import { jsx } from '@emotion/core';

import { StructFlex } from '@cybs-ui/components/lib/struct-flex';
import CybsButton from '@cybs-ui/components/lib/button';
import CybsButtonGroup from '@cybs-ui/components/lib/button-group';
import stylesBuilder from './component.styles';

const CybsFormFooter = ({
  leftButtons,
  rightButtons,
  isFixed,
  style,
  dataTest
}) => {
  const {
    footerStyles,
    wrapperStyles,
    leftBtnGroupStyles,
    rightBtnGroupStyles
  } = stylesBuilder({
    isFixed
  });
  const footerRef = React.createRef();

  const createButton = ({
    dataTest: thisDataTest,
    colorScheme,
    label,
    iconClass,
    iconLocation,
    onClick,
    onKeyDown,
    disabled,
    ...remainingProps
  }) =>
    React.cloneElement(
      <CybsButton dataTest={`${dataTest}-${thisDataTest}`} />,
      {
        colorScheme,
        i18n: { label },
        iconClass,
        iconLocation,
        key: label,
        onClick,
        onKeyDown,
        disabled,
        ...remainingProps
      }
    );

  useEffect(() => {
    // check for ie, isFixed set true, and footer exist (this only runs on IE11)
    if (navigator.userAgent.match(/Trident.*rv:11\./) && isFixed && footerRef) {
      // this should be a REF to the footer
      const footer = footerRef.current;

      // determine footer's parent
      const parent = footer.parentNode;

      // set footer position to fixed
      footer.style.position = 'fixed';

      // set min width to auto
      footer.style.minWidth = 'auto';

      // set footer with equal to parent container on load
      footer.style.width = `${parent.getBoundingClientRect().width}px`;

      // increase space to host footer below card
      footer.previousSibling.style.marginBottom = '76px';

      // crawl up DOM to determine scrolling element since its not window
      let aux = footer.parentNode;
      let prev = footer;
      while (aux.scrollHeight > window.innerHeight) {
        prev = aux;
        aux = aux.parentNode;
      }

      // catch for form being shorter than viewport on load, but fixed set true
      if (prev.scrollTop + prev.offsetHeight === prev.scrollHeight) {
        footer.style.top = `${parent.getBoundingClientRect().bottom}px`;
        footer.style.bottom = 'auto';
      }

      // reposition footer on scroll
      const repositionScroll = () => {
        if (parent.getBoundingClientRect().bottom + 56 > window.innerHeight) {
          footer.style.bottom = '0px';
          footer.style.top = 'auto';
        } else {
          footer.style.top = `${parent.getBoundingClientRect().bottom}px`;
          footer.style.bottom = 'auto';
        }
      };

      // resize footer width on window width changes, reposition footer on window height changes
      const resizeWindow = () => {
        footer.style.width = `${parent.getBoundingClientRect().width}px`;
        repositionScroll();
      };

      prev.addEventListener('scroll', repositionScroll);
      window.addEventListener('resize', resizeWindow);

      resizeWindow();
    }
  }, [footerRef, isFixed]);
  return (
    <div
      css={footerStyles}
      style={style}
      data-test={`${dataTest}-cybsFormFooter`}
      ref={footerRef}
      className="cybs-form-footer"
    >
      <StructFlex
        css={wrapperStyles}
        grow={isFixed ? 1 : 0}
        align-items="center"
        direction="row"
        justify-content={leftButtons ? 'space-between' : 'flex-end'}
      >
        {leftButtons && (
          <CybsButtonGroup
            css={leftBtnGroupStyles}
            dataTest={`${dataTest}-left`}
          >
            {leftButtons.map(createButton)}
          </CybsButtonGroup>
        )}

        {rightButtons && (
          <CybsButtonGroup
            css={rightBtnGroupStyles}
            dataTest={`${dataTest}-right`}
            direction="right"
          >
            {rightButtons.map(createButton)}
          </CybsButtonGroup>
        )}
      </StructFlex>
    </div>
  );
};

CybsFormFooter.propTypes = {
  /**
   * dataTest Label
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * If true, the footer stays at the bottom of the page.
   */
  isFixed: PropTypes.bool,

  /**
   * Buttons on the left side.
   */
  leftButtons: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      dataTest: PropTypes.string.isRequired,
      colorScheme: PropTypes.string,
      iconClass: PropTypes.string,
      iconLocation: PropTypes.oneOf(['left', 'right']),
      label: PropTypes.string,
      onClick: PropTypes.func,
      onKeyDown: PropTypes.func
    })
  ),

  /**
   *Buttons on the right side..
   */
  rightButtons: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      dataTest: PropTypes.string.isRequired,
      colorScheme: PropTypes.string,
      iconClass: PropTypes.string,
      iconLocation: PropTypes.oneOf(['left', 'right']),
      label: PropTypes.string,
      onClick: PropTypes.func,
      onKeyDown: PropTypes.func
    })
  ),

  /**
   * @ignore
   */
  style: PropTypes.objectOf(PropTypes.string)
};

CybsFormFooter.defaultProps = {
  isFixed: false,
  style: {},
  leftButtons: undefined,
  rightButtons: undefined
};

CybsFormFooter.displayName = 'CybsFormFooter';

export default CybsFormFooter;
